import { computed, inject, Injectable, signal } from '@angular/core';
import type { User } from '@/core/user/user.model';
import type { Permission } from '@/core/user/permission.model';
import type { AuthSuccess } from '@/core/user/auth.model';
import { TokenStorageService } from '@/core/storage/token-storage.service';
import { userFromTokens } from '@/core/user/jwt-user';
import { SSO_AUTHORIZE_URL } from '@/core/user/auth.config';

@Injectable({
  providedIn: 'root',
})
export class AuthStore {
  private tokenStorage = inject(TokenStorageService);

  private _currentUser = signal<User | undefined>(undefined);

  public currentUser = this._currentUser.asReadonly();
  public isAuthenticated = computed(() => this._currentUser() !== undefined);

  constructor() {
    this.restoreUser();
  }

  storeAuthResult(result: AuthSuccess): void {
    this.tokenStorage.setIdToken(result.idToken);
    this.tokenStorage.setAccessToken(result.accessToken);
    this.tokenStorage.setRefreshToken(result.refreshToken);
    this.restoreUser();
  }

  logout(): void {
    this.tokenStorage.clearAll();
    this._currentUser.set(undefined);
  }

  hasPermission(permission: Permission): boolean {
    return this._currentUser()?.permissions.includes(permission) ?? false;
  }

  loginWithSso(): void {
    window.location.href = SSO_AUTHORIZE_URL;
  }

  private restoreUser(): void {
    const idToken = this.tokenStorage.getIdToken();
    if (idToken === null || idToken === '') return;

    const accessToken = this.tokenStorage.getAccessToken() ?? '';
    const user = userFromTokens(idToken, accessToken);
    if (!user) {
      this.tokenStorage.clearAll();
      return;
    }

    this._currentUser.set(user);
  }
}
